import React, { useEffect, useState, useMemo } from "react";
import { useLayout } from "context/layout/layout";
import { PortalService } from "services/portal";
import { Tabs, Tab, Box } from "@mui/material";
import { CheckCircle, AlertCircle } from "lucide-react";
import { PasswordField } from "components/PasswordField";
import { PasswordStrengthMeter } from "components/PasswordStrengthMeter";
import { isValidEmail, isValidRut } from "utils/validations";

const CARD = { background: "var(--bg-card)", borderRadius: 12, border: "1px solid var(--border)", overflow: "hidden", padding: 24 };
const inputStyle = {
  width: "100%", padding: "10px 12px", fontSize: 14, border: "1.5px solid var(--border)",
  borderRadius: 8, outline: "none", background: "var(--bg-card)", color: "var(--text-primary)",
  boxSizing: "border-box", transition: "border-color 0.2s",
};
const readonlyStyle = { ...inputStyle, background: "var(--bg-elevated)", color: "var(--text-secondary)", cursor: "not-allowed" };
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 };
const buttonPrimary = (busy) => ({
  width: "100%", padding: "10px", fontSize: 14, fontWeight: 600,
  background: busy ? "#64748b" : "#0f172a", color: "#fff", border: "none",
  borderRadius: 8, cursor: busy ? "not-allowed" : "pointer", marginTop: 16,
});
const buttonSecondary = (busy) => ({
  ...buttonPrimary(busy),
  background: busy ? "#94a3b8" : "#fff",
  color: "var(--text-primary)",
  border: "1.5px solid var(--border)",
});

export function PortalPerfil() {
  const layout = useLayout();
  const [tab, setTab] = useState(0);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Perfil
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState({ name: "", tax_id: "", billing_address: "", business_activity: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  // Password
  const [passwords, setPasswords] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [savingPass, setSavingPass] = useState(false);

  // SII
  const [siiForm, setSiiForm] = useState({ rut: "", clave: "", companyRut: "" });
  const [savingSii, setSavingSii] = useState(false);
  const [testingSii, setTestingSii] = useState(false);
  const [siiTestResult, setSiiTestResult] = useState(null);

  const loadProfile = () => {
    setLoading(true);
    PortalService.getProfile()
      .then((data) => {
        setProfile(data);
        setPhone(data.phone || "");
        setCompany({
          name: data.company_information?.name || "",
          tax_id: data.company_information?.tax_id || "",
          billing_address: data.company_information?.billing_address || "",
          business_activity: data.company_information?.business_activity || "",
        });
        if (data.siiCredentials?.rut) setSiiForm((s) => ({
          ...s,
          rut: data.siiCredentials.rut,
          companyRut: data.siiCredentials.companyRut || "",
        }));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadProfile(); }, []);

  const emailVerified = profile?.emailVerified ?? profile?.portalUser?.emailVerified;

  const handleSaveProfile = async () => {
    if (company.tax_id && !isValidRut(company.tax_id)) {
      return layout.errorAlert({ message: "RUT de empresa inválido" });
    }
    setSavingProfile(true);
    try {
      await PortalService.updateProfile({ phone, company_information: company });
      layout.successAlert({ message: "Perfil actualizado" });
    } catch (err) {
      layout.errorAlert({ message: "Error al actualizar perfil" });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwords.currentPassword || !passwords.newPassword) {
      return layout.errorAlert({ message: "Todos los campos son obligatorios" });
    }
    if (passwords.newPassword.length < 8) {
      return layout.errorAlert({ message: "La nueva contraseña debe tener al menos 8 caracteres" });
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      return layout.errorAlert({ message: "Las contraseñas no coinciden" });
    }
    setSavingPass(true);
    try {
      await PortalService.changePassword({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      layout.successAlert({ message: "Contraseña actualizada" });
      setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      const msg = err?.response?.data?.error || "Error al cambiar contraseña";
      layout.errorAlert({ message: msg });
    } finally {
      setSavingPass(false);
    }
  };

  const handleResendVerification = async () => {
    if (!profile?.email) return;
    try {
      await PortalService.resendVerification({ email: profile.email });
      layout.successAlert({ message: "Email de verificación reenviado" });
    } catch (err) {
      layout.errorAlert({ message: err?.response?.data?.error || "No se pudo reenviar" });
    }
  };

  const handleTestSii = async () => {
    if (!siiForm.rut || !siiForm.clave) {
      return layout.errorAlert({ message: "Ingresa RUT y clave" });
    }
    if (!isValidRut(siiForm.rut)) {
      return layout.errorAlert({ message: "RUT inválido" });
    }
    setTestingSii(true);
    setSiiTestResult(null);
    try {
      const res = await PortalService.testSiiCredentials(siiForm);
      setSiiTestResult(res);
    } catch (err) {
      setSiiTestResult({ ok: false, message: err?.response?.data?.error || err.message });
    } finally {
      setTestingSii(false);
    }
  };

  const handleSaveSii = async () => {
    if (!siiForm.rut || !siiForm.clave) {
      return layout.errorAlert({ message: "Ingresa RUT y clave" });
    }
    if (!isValidRut(siiForm.rut)) {
      return layout.errorAlert({ message: "RUT inválido" });
    }
    setSavingSii(true);
    try {
      await PortalService.saveSiiCredentials(siiForm);
      layout.successAlert({ message: "Credenciales SII guardadas" });
      setSiiForm({ ...siiForm, clave: "" });
      loadProfile();
    } catch (err) {
      layout.errorAlert({ message: err?.response?.data?.error || "Error al guardar" });
    } finally {
      setSavingSii(false);
    }
  };

  const handleDeleteSii = async () => {
    if (!window.confirm("¿Eliminar las credenciales SII guardadas?")) return;
    try {
      await PortalService.deleteSiiCredentials();
      layout.successAlert({ message: "Credenciales eliminadas" });
      setSiiForm({ rut: "", clave: "" });
      loadProfile();
    } catch (err) {
      layout.errorAlert({ message: "Error al eliminar" });
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <p style={{ color: "var(--text-secondary)" }}>Cargando perfil...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>Mi Perfil</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, color: "var(--text-secondary)" }}>{profile?.email}</span>
          {emailVerified ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--positive)", background: "var(--positive-bg)", border: "1px solid #bbf7d0", padding: "2px 8px", borderRadius: 999 }}>
              <CheckCircle size={12} /> Verificado
            </span>
          ) : (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--warning)", background: "var(--warning-bg)", border: "1px solid #fde68a", padding: "2px 8px", borderRadius: 999 }}>
              <AlertCircle size={12} /> Email no verificado
              <button onClick={handleResendVerification} style={{ marginLeft: 4, background: "transparent", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                Reenviar
              </button>
            </span>
          )}
        </div>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Personal" />
          <Tab label="Empresa" />
          <Tab label="Seguridad" />
          <Tab label="SII" />
        </Tabs>
      </Box>

      {tab === 0 && (
        <div style={CARD}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 20 }}>Datos Personales</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <label style={labelStyle}>Nombre</label>
              <input type="text" value={profile?.name || ""} readOnly style={readonlyStyle} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" value={profile?.email || ""} readOnly style={readonlyStyle} />
            </div>
            <div>
              <label style={labelStyle}>RUT</label>
              <input type="text" value={profile?.id_number || ""} readOnly style={readonlyStyle} />
            </div>
            <div>
              <label style={labelStyle}>Teléfono</label>
              <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} placeholder="+56 9 1234 5678" />
            </div>
          </div>
          <button onClick={handleSaveProfile} disabled={savingProfile} style={buttonPrimary(savingProfile)}>
            {savingProfile ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      )}

      {tab === 1 && (
        <div style={CARD}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 20 }}>Datos de Empresa</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <label style={labelStyle}>Razón Social</label>
              <input type="text" value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>RUT Empresa</label>
              <input type="text" value={company.tax_id} onChange={(e) => setCompany({ ...company, tax_id: e.target.value })} style={inputStyle} placeholder="76.123.456-7" />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Dirección de Facturación</label>
              <input type="text" value={company.billing_address} onChange={(e) => setCompany({ ...company, billing_address: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Giro</label>
              <input type="text" value={company.business_activity} onChange={(e) => setCompany({ ...company, business_activity: e.target.value })} style={inputStyle} />
            </div>
          </div>
          <button onClick={handleSaveProfile} disabled={savingProfile} style={buttonPrimary(savingProfile)}>
            {savingProfile ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      )}

      {tab === 2 && (
        <div style={CARD}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 20 }}>Cambiar Contraseña</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 420 }}>
            <div>
              <label style={labelStyle}>Contraseña actual</label>
              <PasswordField
                value={passwords.currentPassword}
                onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                name="currentPassword"
                autoComplete="current-password"
                placeholder="Tu contraseña actual"
              />
            </div>
            <div>
              <label style={labelStyle}>Nueva contraseña</label>
              <PasswordField
                value={passwords.newPassword}
                onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                name="newPassword"
                autoComplete="new-password"
                placeholder="Mínimo 8 caracteres"
              />
              <PasswordStrengthMeter password={passwords.newPassword} />
            </div>
            <div>
              <label style={labelStyle}>Confirmar nueva contraseña</label>
              <PasswordField
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                name="confirmPassword"
                autoComplete="new-password"
                placeholder="Repite tu nueva contraseña"
              />
            </div>
          </div>
          <button onClick={handleChangePassword} disabled={savingPass} style={{ ...buttonPrimary(savingPass), maxWidth: 420 }}>
            {savingPass ? "Guardando..." : "Cambiar contraseña"}
          </button>
          {profile?.lastLogin && (
            <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 20 }}>
              Último acceso: {new Date(profile.lastLogin).toLocaleString("es-CL")}
            </p>
          )}
        </div>
      )}

      {tab === 3 && (
        <div style={CARD}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 8 }}>Credenciales SII</h3>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 20 }}>
            Tus credenciales del SII se guardan encriptadas (AES-256-GCM) y se usan solo para
            sincronizar tu Registro de Compras y Ventas.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 600 }}>
            <div>
              <label style={labelStyle}>RUT autenticador</label>
              <input
                type="text"
                value={siiForm.rut}
                onChange={(e) => setSiiForm({ ...siiForm, rut: e.target.value })}
                style={inputStyle}
                placeholder="12.345.678-9"
              />
              <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                El RUT con el que inicias sesión en el SII (puede ser persona natural).
              </p>
            </div>
            <div>
              <label style={labelStyle}>Clave SII</label>
              <PasswordField
                value={siiForm.clave}
                onChange={(e) => setSiiForm({ ...siiForm, clave: e.target.value })}
                name="sii-clave"
                autoComplete="off"
                placeholder={profile?.siiConfigured ? "(no se muestra; ingresa para cambiar)" : "Tu clave del SII"}
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>RUT empresa (opcional)</label>
              <input
                type="text"
                value={siiForm.companyRut}
                onChange={(e) => setSiiForm({ ...siiForm, companyRut: e.target.value })}
                style={inputStyle}
                placeholder="76.123.456-7"
              />
              <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                Si tu RUT autenticador tiene varias empresas asociadas, indica acá el
                RUT de la empresa cuyos registros quieres traer. Si no, deja en blanco.
              </p>
            </div>
          </div>

          {siiTestResult && (
            <div
              style={{
                marginTop: 16, padding: "10px 14px", borderRadius: 8,
                background: siiTestResult.ok ? "#ecfdf5" : "#fef2f2",
                border: `1px solid ${siiTestResult.ok ? "#bbf7d0" : "#fecaca"}`,
                color: siiTestResult.ok ? "#065f46" : "#991b1b",
                fontSize: 13,
                maxWidth: 600,
              }}
            >
              {siiTestResult.ok
                ? "✓ Credenciales válidas. Puedes guardarlas."
                : `✗ ${siiTestResult.message || "Credenciales inválidas"}`}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 16, maxWidth: 600 }}>
            <button onClick={handleTestSii} disabled={testingSii || savingSii} style={{ ...buttonSecondary(testingSii), marginTop: 0, flex: 1 }}>
              {testingSii ? "Probando..." : "Probar credenciales"}
            </button>
            <button onClick={handleSaveSii} disabled={savingSii || testingSii} style={{ ...buttonPrimary(savingSii), marginTop: 0, flex: 1 }}>
              {savingSii ? "Guardando..." : "Guardar"}
            </button>
          </div>

          {profile?.siiConfigured && (
            <button
              onClick={handleDeleteSii}
              style={{
                marginTop: 24, background: "transparent", border: "none",
                color: "var(--negative)", fontSize: 13, fontWeight: 500, cursor: "pointer", padding: 0,
              }}
            >
              Eliminar credenciales guardadas
            </button>
          )}
        </div>
      )}
    </div>
  );
}
